import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Flex, Input, Text } from '@/app/ui/atoms';
import Colors from '@/app/styles/Colors';
import { translate } from '@/app/i18n';

interface FileUploadComponentProps {
  onChange: (value: File) => void;
  type?: 'audio' | 'image';
  existingFile?: string;
  subtitle?: string;
  mandatoryError?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MIN_IMAGE_DIMENSION = 1400; // 1400px
const MAX_MEGAPIXELS = 50; // 50MP

const FileUploadComponent = (props: FileUploadComponentProps) => {
  const { onChange, type, existingFile, subtitle, mandatoryError } = props;
  const [file, setFile] = useState<File>();
  const [allowedExtensions, setAllowedExtensions] = useState<string | undefined>();

  async function validateImage(imageFile: File): Promise<boolean> {
    if (imageFile.size > MAX_FILE_SIZE) {
      toast.error('Image size must be less than 10MB');
      return false;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const { width } = img;
        const { height } = img;
        const megapixels = (width * height) / 1000000;

        if (width !== height) {
          toast.error('Image must be square');
          resolve(false);
          return;
        }

        if (width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION) {
          toast.error(
            `Image dimensions must be at least ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION}px`,
          );
          resolve(false);
          return;
        }

        if (megapixels > MAX_MEGAPIXELS) {
          toast.error(`Image cannot exceed ${MAX_MEGAPIXELS} megapixels`);
          resolve(false);
          return;
        }

        resolve(true);
      };

      img.onerror = () => {
        toast.error('Error loading image');
        resolve(false);
      };

      img.src = URL.createObjectURL(imageFile);
    });
  }

  async function handeFileUpload(f: File) {
    const extension = f.name.split('.').pop();
    if (allowedExtensions && extension && !allowedExtensions.includes(extension)) {
      toast.error(`Invalid ${type} format.`);
      return;
    }

    if (type === 'image') {
      const isValid = await validateImage(f);
      if (!isValid) return;
    }

    setFile(f);
    onChange(f);
  }

  function dropHandler(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();

    if (ev.dataTransfer && ev.dataTransfer.files[0]) {
      handeFileUpload(ev.dataTransfer.files[0]);
    }
  }

  function dragOverHandler(ev: React.DragEvent<HTMLDivElement>) {
    ev.preventDefault();
  }

  useEffect(() => {
    switch (type) {
      case 'image':
        setAllowedExtensions('.jpeg,.jpg');
        break;
      case 'audio':
        setAllowedExtensions('.wav');
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    async function getFile() {
      if (!existingFile) return;
      const response = await fetch(existingFile);
      const data = await response.blob();
      const metadata = {
        type,
      };
      const filename = existingFile.split('/');
      const f = new File([data], filename[filename.length - 1], metadata);
      setFile(f);
    }
    getFile();
  }, []);

  return (
    <div onDrop={dropHandler} onDragOver={dragOverHandler}>
      <Flex.Container
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap="2rem"
        style={{
          position: 'relative',
          backgroundColor: Colors.backgroundSidebar,
          padding: '2.5rem',
          border: mandatoryError ? `1px solid ${Colors.red}` : '',
        }}
      >
        {subtitle && <Text.LabelSmall style={{ color: Colors.grey }}>{subtitle}</Text.LabelSmall>}
        <Flex.Container gap="1rem" alignItems="center">
          <Input.File
            id="fileInput"
            onChange={(e) => e.target.files && handeFileUpload(e.target.files[0])}
            hidden
            accept={allowedExtensions}
          />
          <Text.LabelMedium htmlFor="fileInput">
            <Text.SectionSubtitleBold
              style={{
                color: Colors.primary,
                border: `1px solid ${Colors.primary}`,
                padding: '1rem 2rem',
                cursor: 'pointer',
              }}
            >
              {translate('WORKS.UPLOAD')}
            </Text.SectionSubtitleBold>
          </Text.LabelMedium>
          {file === undefined ? (
            <Text.LabelSmall style={{ color: Colors.grey }}>
              {translate('WORKS.DROP_FILE')}
            </Text.LabelSmall>
          ) : (
            <Text.LabelSmall style={{ color: Colors.grey }}>{file.name}</Text.LabelSmall>
          )}
        </Flex.Container>
      </Flex.Container>
    </div>
  );
};

export default FileUploadComponent;
